import { useCallback, useLayoutEffect, useRef, useState } from "react";
import type { ReactElement } from "react";
import type { TableProps } from "react-data-table-component";
import { Loader2 } from "lucide-react";

type HeightValue = number | string;

type ScrollableTableOptions = {
  /**
   * Height budget for the full table block.
   * - omitted: auto-compute from viewport remaining space
   * - number: treated as pixel height
   * - string: used as CSS height value (for example "70vh")
   */
  readonly height?: HeightValue;
  /** Space to reserve below the table block when auto-calculating height. */
  readonly bottomOffset?: number;
  /** Space used by table chrome (pagination/footer) in fixed-header calculations. */
  readonly chromeOffset?: number;
  /** Minimum body height used by the fixed header scroll region. */
  readonly minBodyHeight?: number;
};

type ScrollableTableInjectedProps = ScrollableTableOptions & {
  readonly fixedHeader?: boolean;
  readonly fixedHeaderScrollHeight?: string;
};

type DataTableComponent = <TRow>(
  props: TableProps<TRow>,
) => ReactElement | null;

const DEFAULT_BOTTOM_OFFSET = 24;
const DEFAULT_CHROME_OFFSET = 72;
const DEFAULT_MIN_BODY_HEIGHT = 180;

const defaultProgressComponent = (
  <div
    className="flex min-h-[220px] w-full items-center justify-center"
    role="status"
    aria-live="polite"
    aria-label="Loading table data"
  >
    <div className="inline-flex items-center gap-3 rounded-full border border-primary-100 bg-primary-50/60 px-4 py-2.5 shadow-sm">
      <Loader2 className="h-4 w-4 animate-spin text-primary-500" aria-hidden />
      <span className="text-sm font-medium text-gray-700">Loading data</span>
    </div>
  </div>
);

const toCssHeight = (value: HeightValue): string =>
  typeof value === "number" ? `${value}px` : value;

const toFixedHeaderHeight = (height: string, chromeOffset: number): string =>
  chromeOffset > 0 ? `calc(${height} - ${chromeOffset}px)` : height;

export function withScrollableDataTable(BaseDataTable: DataTableComponent) {
  const ScrollableDataTable = <TRow,>({
    height,
    bottomOffset = DEFAULT_BOTTOM_OFFSET,
    chromeOffset = DEFAULT_CHROME_OFFSET,
    minBodyHeight = DEFAULT_MIN_BODY_HEIGHT,
    fixedHeader,
    fixedHeaderScrollHeight,
    ...props
  }: TableProps<TRow> & ScrollableTableInjectedProps) => {
    const hostRef = useRef<HTMLDivElement>(null);
    const [autoHeight, setAutoHeight] = useState<string>("480px");

    const syncAutoHeight = useCallback(() => {
      if (!hostRef.current || typeof window === "undefined") {
        return;
      }

      const { top } = hostRef.current.getBoundingClientRect();
      const availableHeight = Math.floor(window.innerHeight - top - bottomOffset);
      const nextHeight = Math.max(minBodyHeight + chromeOffset, availableHeight);
      setAutoHeight(`${nextHeight}px`);
    }, [bottomOffset, chromeOffset, minBodyHeight]);

    useLayoutEffect(() => {
      if (height !== undefined) {
        return;
      }

      syncAutoHeight();
      window.addEventListener("resize", syncAutoHeight);
      return () => {
        window.removeEventListener("resize", syncAutoHeight);
      };
    }, [height, syncAutoHeight]);

    const resolvedHeight = height === undefined ? autoHeight : toCssHeight(height);
    const resolvedScrollHeight = toFixedHeaderHeight(resolvedHeight, chromeOffset);
    const fixedHeaderEnabled = fixedHeader ?? true;

    const hostClassName =
      height === "100%"
        ? "min-h-0 h-full w-full flex-1 overflow-hidden"
        : "min-h-0 flex-1 overflow-hidden";

    return (
      <div ref={hostRef} className={hostClassName}>
        <BaseDataTable
          {...(props as TableProps<TRow>)}
          fixedHeader={fixedHeaderEnabled}
          fixedHeaderScrollHeight={fixedHeaderScrollHeight ?? resolvedScrollHeight}
          progressComponent={props.progressComponent ?? defaultProgressComponent}
        />
      </div>
    );
  };

  ScrollableDataTable.displayName = "WithScrollableDataTable";
  return ScrollableDataTable;
}
